<!--
  One item on the playout timeline. Not a calendar entry: an operator must see at a
  glance what has aired, what is on air, what is queued and what is locked, which items
  are hard-timed, and which carry a warning.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { formatClock } from '../clock.js';
import type { PlayoutItemLabels } from '../labels.js';
import {
  CONTENT_PRESENTATION,
  PLAYOUT_ITEM_LABEL,
  type ContentType,
  type PlayoutItemState,
} from '../onair.js';
import { formatTimecode } from '../onair.js';
import { ICONS, icon } from '../icons.js';

const props = defineProps<{
  title: string;
  subtitle?: string | null;
  contentType: ContentType;
  state: PlayoutItemState;
  startsAt: string;
  durationMs?: number | null;
  isExactTime?: boolean;
  warning?: string | null;
  /** Overrides the station timezone. Rarely needed; the default is the station's. */
  timezone?: string;
  /**
   * Translated words, passed in. Anything missing falls back to the English in
   * `onair.ts`, so a caller that has no translation still gets a readable row.
   */
  labels?: PlayoutItemLabels;
}>();

const content = computed(() => CONTENT_PRESENTATION[props.contentType]);
const IconComponent = computed(() => icon(content.value.icon));
const clock = computed(() => formatClock(props.startsAt, props.timezone));
const typeLabel = computed(
  () => props.labels?.contentTypes?.[props.contentType] ?? content.value.label,
);
const stateLabel = computed(
  () => props.labels?.states?.[props.state] ?? PLAYOUT_ITEM_LABEL[props.state],
);
const exactTimeLabel = computed(() => props.labels?.exactTime ?? 'Exact-time item');
const warningLabel = computed(() => props.labels?.warning ?? 'Warning');
</script>

<template>
  <article
    class="shio-playout-item"
    :data-state="state"
    :style="{ '--item-hue': `var(${content.token})` }"
  >
    <span class="shio-playout-item__time shio-numeric">
      {{ clock }}
      <component
        :is="ICONS.Pin"
        v-if="isExactTime"
        :size="12"
        class="shio-playout-item__exact"
        aria-hidden="true"
      />
      <span v-if="isExactTime" class="shio-sr-only">{{ exactTimeLabel }}</span>
    </span>

    <component :is="IconComponent" :size="16" class="shio-playout-item__icon" aria-hidden="true" />

    <div class="shio-playout-item__text">
      <p class="shio-playout-item__title">{{ title }}</p>
      <p v-if="subtitle" class="shio-meta">{{ subtitle }}</p>
    </div>

    <span class="shio-meta">{{ typeLabel }}</span>

    <span v-if="durationMs" class="shio-meta shio-tabular">{{ formatTimecode(durationMs) }}</span>

    <span
      class="shio-badge"
      :class="state === 'blocked' ? 'shio-badge--danger' : 'shio-badge--neutral'"
    >
      {{ stateLabel }}
    </span>

    <span v-if="warning" class="shio-playout-item__warning" :title="warning">
      <component :is="ICONS.TriangleAlert" :size="14" aria-hidden="true" />
      <span class="shio-sr-only">{{ warningLabel }}: {{ warning }}</span>
    </span>
  </article>
</template>

<style scoped>
.shio-playout-item {
  display: grid;
  grid-template-columns: 64px 20px 1fr auto auto auto 20px;
  align-items: center;
  gap: var(--shio-space-3);
  /* A minimum rather than a fixed height: an item with a subtitle is two lines, and
     clipping the second one loses "Rerun" or "Crosses midnight". */
  min-height: var(--shio-row-height);
  padding: var(--shio-space-1) var(--shio-space-3);
  border-left: 3px solid hsl(var(--item-hue));
  border-bottom: var(--shio-border-width) solid hsl(var(--shio-border) / 0.4);
  background: hsl(var(--shio-surface-card));
}

.shio-playout-item__icon {
  color: hsl(var(--item-hue));
}

.shio-playout-item__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
}

.shio-playout-item__text p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shio-playout-item__title {
  margin: 0;
  font-size: var(--shio-type-table-size);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shio-playout-item__time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--shio-type-table-size);
}

.shio-playout-item__exact {
  color: hsl(var(--shio-event-exact-time));
}

.shio-playout-item__warning {
  color: hsl(var(--shio-status-warning));
}

/* Already aired: dimmed, but still readable. */
.shio-playout-item[data-state='aired'] {
  opacity: 0.55;
}

/* On air: the one strongly marked row. */
.shio-playout-item[data-state='on_air'] {
  background: hsl(var(--shio-status-live) / 0.1);
  border-left-color: hsl(var(--shio-status-live));
  box-shadow: inset 0 0 0 1px hsl(var(--shio-status-live) / 0.35);
}

/* Staged and no longer editable. */
.shio-playout-item[data-state='locked'] {
  background: hsl(var(--shio-surface-card));
  border-left-style: double;
  border-left-width: 4px;
}

.shio-playout-item[data-state='blocked'] {
  background: hsl(var(--shio-status-danger) / 0.08);
}

@media (max-width: 767px) {
  .shio-playout-item {
    /* The warning marker keeps its own column rather than wrapping onto a second row,
       where it reads as belonging to the next item. */
    grid-template-columns: 56px 18px 1fr auto 18px;
    height: auto;
    padding: var(--shio-space-2) var(--shio-space-3);
  }

  /* Content label and duration collapse before anything operational. */
  .shio-playout-item > span.shio-meta:not(.shio-playout-item__time) {
    display: none;
  }
}
</style>
